import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { DialogRef } from '@angular/cdk/dialog';
import {
  AppSvgIconComponent,
  BaseOverlayComponent,
  OverlayService,
  SignalTextField,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { DataLibraryListItemResponse } from '@core/api/workspace/models/dataLibraryListItemResponse';
import type { ListDataLibraryApiResponse } from '@core/api/workspace/models/listDataLibraryApiResponse';
import { DataLibraryListItemResponseKind } from '@core/api/workspace/models/dataLibraryListItemResponseKind';
import { CollectionDataPreviewSheetComponent } from '../collection-data-preview-sheet/collection-data-preview-sheet';

export interface CollectionPickerResult {
  id: string;
  name: string;
  key: string;
  itemCount?: number;
}

@Component({
  selector: 'app-collection-picker-overlay',
  standalone: true,
  imports: [AppSvgIconComponent, BaseOverlayComponent, SignalTextField],
  templateUrl: './collection-picker-overlay.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionPickerOverlayComponent
  extends BaseComponent
  implements OnInit
{
  private readonly dialogRef = inject(
    DialogRef<CollectionPickerResult | null>,
  );
  private readonly workspaceApi = inject(WorkspaceAPIService);
  private readonly overlayService = inject(OverlayService);

  readonly listState = new State<ListDataLibraryApiResponse>();
  readonly search = signal('');
  readonly items = signal<CollectionPickerResult[]>([]);

  readonly filteredItems = computed(() => {
    const query = this.search().trim().toLowerCase();
    if (!query) {
      return this.items();
    }
    return this.items().filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.key.toLowerCase().includes(query),
    );
  });

  readonly isLoading = computed(() => this.listState.loading());

  readonly isEmpty = computed(
    () => !this.isLoading() && this.filteredItems().length === 0,
  );

  ngOnInit(): void {
    this.loadCollections();
  }

  onSearchChange(value: string): void {
    this.search.set(value);
    this.loadCollections(value);
  }

  selectCollection(item: CollectionPickerResult): void {
    this.dialogRef.close(item);
  }

  previewCollection(item: CollectionPickerResult, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.overlayService.openBottomSheet(CollectionDataPreviewSheetComponent, {
      data: {
        collectionId: item.id,
        collectionName: item.name,
      },
      panelClass: [
        'bg-white',
        'shadow-2',
        'w-full',
        'h-[90%]',
        'max-h-[90dvh]',
        'rounded-t-3xl',
        'overflow-hidden',
      ],
    });
  }

  private loadCollections(search = this.search()): void {
    const trimmed = search.trim();
    this.executeRequest({
      state: this.listState,
      request: this.workspaceApi.listDataLibrary({
        ...(trimmed ? { search: trimmed } : {}),
        pageNumber: 1,
        pageSize: 100,
      }),
      handleError: true,
      onSuccess: (response: ListDataLibraryApiResponse) => {
        const mapped = (response.data?.items ?? [])
          .filter(
            (item: DataLibraryListItemResponse) =>
              item.kind === DataLibraryListItemResponseKind.collection,
          )
          .map(
            (item: DataLibraryListItemResponse): CollectionPickerResult => ({
              id: item.id,
              name: item.name,
              key: item.key,
              itemCount: item.itemCount,
            }),
          );
        this.items.set(mapped);
      },
    });
  }
}
